import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { ScrollRestoration } from 'react-router-dom';
import GlobalAlertDialog, {
  type AlertDialogData,
} from '@/components/GlobalAlertDialog';

export type HeadlineData = {
  hidden?: boolean;
  inline?: boolean;
  noLanguages?: boolean;
  subtitle?: string;
  title?: string;
};

type AppContextType = {
  additionalData: ReactNode | null;
  appTitle: string;
  headline: HeadlineData | null;
  setAdditionalData: Dispatch<SetStateAction<ReactNode | null>>;
  setAlertDialog: (data?: AlertDialogData) => void;
  setAppTitle: (title: string, level?: number | 'pop') => void;
  setHeadline: (data: HeadlineData | null) => void;
};

const AppContext = createContext<AppContextType>({
  additionalData: null,
  appTitle: 'Swipesign',
  headline: null,
  setAdditionalData: () => {},
  setAlertDialog: () => {},
  setAppTitle: () => {},
  setHeadline: () => {},
});

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [additionalData, setAdditionalData] = useState<ReactNode | null>(null);
  const [appTitleInternal, setAppTitleInternal] = useState<string[]>([]);
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertDialogData>({});
  const [headline, setHeadline] = useState<HeadlineData | null>(null);

  const setAppTitle = (title: string, level: number | 'pop' = 0) => {
    setAppTitleInternal((prevState) => {
      const newState = [...prevState];

      if (level === 'pop') {
        const index = newState.findIndex((entry) => {
          return entry === title;
        });
        newState[index] = '';
      } else {
        newState[level] = title;
      }

      return newState;
    });
  };

  const setAlertDialog = (data?: AlertDialogData) => {
    if (data) {
      setAlertDialogOpen(true);
      setAlertDialogData(data);
    } else {
      setAlertDialogOpen(false);
    }
  };

  useEffect(() => {
    document.title = [
      ...appTitleInternal.toReversed().filter(Boolean),
      'swipesign',
    ].join(' | ');
  }, [appTitleInternal]);

  const contextValue: AppContextType = {
    additionalData,
    appTitle: appTitleInternal.filter(Boolean).join(' | '),
    headline,
    setAdditionalData,
    setAlertDialog,
    setAppTitle,
    setHeadline,
  };

  return (
    <AppContext.Provider value={contextValue}>
      <ScrollRestoration />
      {children}
      <GlobalAlertDialog
        isOpen={alertDialogOpen}
        setIsOpen={setAlertDialogOpen}
        {...alertDialogData}
      />
    </AppContext.Provider>
  );
};

const useApp = (): AppContextType => {
  const ctx = useContext(AppContext);

  if (!ctx) {
    throw new Error('useApp must be used within AppProvider');
  }

  return ctx;
};

export default useApp;
